import { useState, useEffect } from 'react'
import { RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useProcessingStatus } from '@/hooks/useProcessingStatus'
import { useTriggerRecalculation } from '@/hooks/useStoreModels'
import { toast } from '@/stores/useToastStore'
import type { ProcessingResponse } from '@/types'

interface RecalculationSectionProps {
  modelId: string
  onRecalculationComplete?: () => void
}

export function RecalculationSection({
  modelId,
  onRecalculationComplete,
}: RecalculationSectionProps) {
  const [processingResponse, setProcessingResponse] =
    useState<ProcessingResponse | null>(null)
  const processingStatus = useProcessingStatus(processingResponse)
  const recalcMutation = useTriggerRecalculation(modelId)

  useEffect(() => {
    if (processingStatus.state === 'complete') {
      toast.success('Recalculation complete')
      setProcessingResponse(null)
      processingStatus.reset()
      onRecalculationComplete?.()
    } else if (processingStatus.state === 'error') {
      toast.error(processingStatus.errorMessage ?? 'Recalculation failed')
      setProcessingResponse(null)
      processingStatus.reset()
    }
  }, [processingStatus.state]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleTrigger = () => {
    recalcMutation.mutate(undefined, {
      onSuccess: data => {
        setProcessingResponse(data)
        toast.success('Recalculation started')
      },
      onError: error => {
        const detail = (error.response?.data as { detail?: string } | undefined)
          ?.detail
        toast.error(detail ?? 'Failed to trigger recalculation')
      },
    })
  }

  const isProcessing =
    processingStatus.state === 'processing' || recalcMutation.isPending

  return (
    <div className="flex items-center gap-3">
      <Button
        onClick={handleTrigger}
        disabled={isProcessing}
        className="cursor-pointer"
      >
        <RefreshCw
          className={`h-4 w-4 mr-2 ${isProcessing ? 'animate-spin' : ''}`}
        />
        Trigger Recalculation
      </Button>
      <span className="text-xs text-muted-foreground">
        Re-fetch sales data and recalculate allocation percentages
      </span>
    </div>
  )
}
